import { useEffect, useState } from "react";
import api, { errMsg, downloadReport } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ExternalLink, Shield, Upload } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { MODULE_GROUPS, ALL_MODULE_IDS } from "../lib/modules";
import { HierarchyTree } from "../components/HierarchyTree";
import { CompanyDocsSection } from "../components/CompanyDocsSection";
import { MySignatureSection } from "../components/MySignatureSection";
import { SenderConfig } from "../components/SenderConfig";
import { ResendDomains } from "../components/ResendDomains";
import { DigestSettings } from "../components/DigestSettings";
import { BulkUsersDialog } from "../components/BulkUsersDialog";
import { HandoverSection } from "../components/HandoverSection";
import { PoApproversSection } from "../components/PoApproversSection";

const PRESETS = [
  { name: "Rojo", h: 0, s: 100, l: 45 },
  { name: "Azul", h: 220, s: 90, l: 50 },
  { name: "Verde", h: 150, s: 80, l: 35 },
  { name: "Naranja", h: 25, s: 95, l: 50 },
  { name: "Violeta", h: 270, s: 70, l: 50 },
  { name: "Negro", h: 0, s: 0, l: 10 },
];

const ROLES = ["admin", "buyer", "approver", "warehouse", "maintenance", "viewer", "supplier"];

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { user, can } = useAuth();
  const [settings, setSettings] = useState(null);
  const [dark, setDark] = useState(localStorage.getItem("erp_dark") === "1");
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [backups, setBackups] = useState([]);
  const [bkSchedule, setBkSchedule] = useState(null);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });
  const [departments, setDepartments] = useState([]);
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ code: "", name: "", active: true, default_warehouse: "" });
  const [deptEditId, setDeptEditId] = useState(null);
  const [pmAuto, setPmAuto] = useState({ enabled: true, hours_before: 50, check_interval_minutes: 60 });
  const [rotation, setRotation] = useState({ vigente_max_days: 90, lento_max_days: 180, strategic_items: [] });
  const [resPreview, setResPreview] = useState(null);
  const [resHistory, setResHistory] = useState([]);
  const [uOpen, setUOpen] = useState(false);
  const [uForm, setUForm] = useState({ email: "", password: "", name: "", role: "viewer", supplier_id: "", position: "", send_welcome: true });
  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permForm, setPermForm] = useState({
    role: "viewer", supervisor_id: "", approval_limit: 0,
    approval_currency: "MXN", visible_modules: [], name: "",
    new_password: "", active: true,
  });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
    if (can("admin")) {
      api.get("/users").then((r) => setUsers(r.data));
      api.get("/address-book", { params: { type: "supplier" } }).then((r) => setSuppliers(r.data));
      api.get("/budgets").then((r) => setBudgets(r.data));
      api.get("/currencies").then((r) => setCurrencies(r.data));
      api.get("/backups").then((r) => setBackups(r.data));
      api.get("/backups/schedule").then((r) => setBkSchedule(r.data));
    }
    api.get("/departments").then((r) => setDepartments(r.data)).catch(() => {});
    if (can("admin") || can("maintenance")) {
      api.get("/mnt/pm-auto-schedule").then((r) => setPmAuto(r.data)).catch(() => {});
    }
    if (can("admin")) {
      api.get("/stock-rotation/settings").then((r) => setRotation(r.data)).catch(() => {});
      // Fire-and-forget: preload reservation preview
      api.get("/inventory/reservation-cleanup/preview").then((r) => setResPreview(r.data)).catch(() => {});
      api.get("/inventory/reservation-cleanup/history").then((r) => setResHistory(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const changePassword = async () => {
    try {
      await api.post("/auth/change-password", pwd);
      toast.success(t("set.passwordChanged"));
      setPwd({ current_password: "", new_password: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runBackup = async () => {
    try {
      await api.post("/backups/run");
      toast.success(t("bk.done"));
      api.get("/backups").then((r) => setBackups(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restoreBackup = async (name) => {
    if (!window.confirm(t("bk.restoreConfirm"))) return;
    try {
      const { data } = await api.post(`/backups/${name}/restore`);
      toast.success(`${t("bk.restored")} (${t("bk.safety")}: ${data.safety_backup})`);
      api.get("/backups").then((r) => setBackups(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const restoreUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    if (!window.confirm(t("bk.restoreConfirm"))) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/backups/restore-upload", fd);
      toast.success(`${t("bk.restored")} (${t("bk.safety")}: ${data.safety_backup})`);
      api.get("/backups").then((r) => setBackups(r.data));
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveBkSchedule = async (patch) => {
    const next = { ...bkSchedule, ...patch };
    setBkSchedule(next);
    try {
      const { data } = await api.put("/backups/schedule", { enabled: next.enabled, interval_hours: Number(next.interval_hours) });
      setBkSchedule(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveCurrencies = async () => {
    try {
      await api.put("/currencies", { currencies: currencies.filter((c) => c.code).map((c) => ({ code: c.code.toUpperCase(), rate_to_base: Number(c.rate_to_base) })) });
      toast.success(t("common.saved"));
      api.get("/currencies").then((r) => setCurrencies(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveBudgets = async () => {
    try {
      await api.put("/budgets", { budgets: budgets.filter((b) => b.department).map((b) => ({ department: b.department, monthly_budget: Number(b.monthly_budget) })) });
      toast.success(t("common.saved"));
      api.get("/budgets").then((r) => setBudgets(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const savePmAuto = async () => {
    try {
      const { data } = await api.put("/mnt/pm-auto-schedule", {
        enabled: !!pmAuto.enabled,
        hours_before: Number(pmAuto.hours_before) || 0,
        check_interval_minutes: Number(pmAuto.check_interval_minutes) || 60,
      });
      setPmAuto(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runPmSchedulerNow = async () => {
    try {
      const { data } = await api.post("/mnt/pm-auto-schedule/run");
      if (data.skipped) toast.info("Scheduler está desactivado");
      else toast.success(`Ejecutado · ${data.count} aviso(s) creado(s)`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveRotation = async () => {
    try {
      const clean = {
        vigente_max_days: Number(rotation.vigente_max_days),
        lento_max_days: Number(rotation.lento_max_days),
        strategic_items: (typeof rotation.strategic_items === "string"
          ? rotation.strategic_items.split(/[\s,;]+/).filter(Boolean)
          : (rotation.strategic_items || [])),
      };
      const { data } = await api.put("/stock-rotation/settings", clean);
      setRotation(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadResPreview = async () => {
    try {
      const [{ data: p }, { data: h }] = await Promise.all([
        api.get("/inventory/reservation-cleanup/preview"),
        api.get("/inventory/reservation-cleanup/history"),
      ]);
      setResPreview(p);
      setResHistory(h);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const runResCleanup = async () => {
    if (!window.confirm("¿Ejecutar aseo? Liberará reservas huérfanas ahora.")) return;
    try {
      const { data } = await api.post("/inventory/reservation-cleanup/run");
      toast.success(`Liberadas ${data.total_released || 0} unidades en ${data.released?.length || 0} balances`);
      loadResPreview();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const applyColor = (h, s, l) => {
    document.documentElement.style.setProperty("--primary", `${h} ${s}% ${l}%`);
    document.documentElement.style.setProperty("--ring", `${h} ${s}% ${l}%`);
  };

  const saveTheme = async (preset) => {
    const next = { ...settings, primary_hue: preset.h, primary_sat: preset.s, primary_light: preset.l };
    setSettings(next);
    applyColor(preset.h, preset.s, preset.l);
    try {
      await api.put("/settings", next);
      toast.success(t("set.colorSaved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveCompany = async () => {
    try {
      await api.put("/settings", settings);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleDark = (v) => {
    setDark(v);
    document.documentElement.classList.toggle("dark", v);
    localStorage.setItem("erp_dark", v ? "1" : "0");
  };

  const createUser = async () => {
    try {
      const { data } = await api.post("/users", uForm);
      toast.success(data.welcome_email ? `${t("common.created")} · ${t("set.bulkWelcomeStatus")}: ${data.welcome_email}` : t("common.created"));
      setUOpen(false);
      setUForm({ email: "", password: "", name: "", role: "viewer", supplier_id: "", position: "", send_welcome: true });
      api.get("/users").then((r) => setUsers(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openPermissions = (u) => {
    setPermUser(u);
    setPermForm({
      role: u.role || "viewer",
      supervisor_id: u.supervisor_id || "",
      approval_limit: Number(u.approval_limit || 0),
      approval_currency: u.approval_currency || "MXN",
      visible_modules: Array.isArray(u.visible_modules) ? u.visible_modules : [],
      name: u.name || "",
      position: u.position || "",
      new_password: "",
      active: u.active !== false,
    });
    setPermOpen(true);
  };

  const savePermissions = async () => {
    try {
      const payload = { ...permForm };
      if (!payload.new_password?.trim()) delete payload.new_password;
      await api.put(`/users/${permUser.id}/permissions`, payload);
      toast.success(t("common.saved"));
      setPermOpen(false);
      setPermUser(null);
      api.get("/users").then((r) => setUsers(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const togglePermModule = (id) => {
    setPermForm((p) => ({
      ...p,
      visible_modules: p.visible_modules.includes(id)
        ? p.visible_modules.filter((x) => x !== id)
        : [...p.visible_modules, id],
    }));
  };

  const togglePermGroup = (group, allOn) => {
    const ids = group.modules.map((m) => m.id);
    setPermForm((p) => ({
      ...p,
      visible_modules: allOn
        ? p.visible_modules.filter((x) => !ids.includes(x))
        : [...new Set([...p.visible_modules, ...ids])],
    }));
  };

  if (!settings) return <div className="text-xs text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <PageHeader title={t("set.title")} subtitle={t("set.subtitle")} />

      <div className="border bg-card p-5 mb-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">{t("set.appearance")}</div>
        <div className="flex items-center justify-between mb-4">
          <Label className="text-xs font-bold">{t("set.darkMode")}</Label>
          <Switch checked={dark} onCheckedChange={toggleDark} data-testid="settings-dark-switch" />
        </div>
        <div className="flex items-center justify-between mb-4">
          <Label className="text-xs font-bold">{t("set.language")}</Label>
          <div className="flex border">
            {["es", "en"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`settings-lang-${l}`}
                className={`px-3 py-1 text-[10px] font-bold uppercase ${lang === l ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{l}</button>
            ))}
          </div>
        </div>
        {can("admin") && (
          <>
            <Label className="text-xs font-bold block mb-2">{t("set.primaryColor")}</Label>
            <div className="flex gap-2 flex-wrap mb-4">
              {PRESETS.map((p) => (
                <button key={p.name} onClick={() => saveTheme(p)} data-testid={`color-preset-${p.name.toLowerCase()}`}
                  className={`w-14 h-10 border-2 transition-transform active:scale-95 flex items-end justify-center pb-0.5 text-[8px] font-bold text-white ${
                    settings.primary_hue === p.h && settings.primary_light === p.l ? "border-foreground" : "border-transparent"}`}
                  style={{ background: `hsl(${p.h} ${p.s}% ${p.l}%)` }}>
                  {p.name}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-end">
              <div className="space-y-1 flex-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.companyName")}</Label>
                <Input value={settings.company_name} onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  className="rounded-none h-8 text-xs" data-testid="settings-company-input" />
              </div>
              <Button size="sm" onClick={saveCompany} className="rounded-none text-xs font-bold" data-testid="settings-save-company">{t("common.save")}</Button>
            </div>
            <div className="flex gap-2 items-end mt-3">
              <div className="space-y-1 flex-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("st.brandTagline")}</Label>
                <Input value={settings.brand_tagline ?? ""} maxLength={60}
                  onChange={(e) => setSettings({ ...settings, brand_tagline: e.target.value })}
                  className="rounded-none h-8 text-xs" data-testid="settings-tagline-input" />
              </div>
              <div className="space-y-1 flex-1">
                <Label className="text-[10px] font-bold uppercase tracking-widest">{t("st.appSubtitle")}</Label>
                <Input value={settings.app_subtitle ?? ""} maxLength={80}
                  onChange={(e) => setSettings({ ...settings, app_subtitle: e.target.value })}
                  className="rounded-none h-8 text-xs" data-testid="settings-subtitle-input" />
              </div>
              <Button size="sm" onClick={saveCompany} className="rounded-none text-xs font-bold" data-testid="settings-save-brand">{t("common.save")}</Button>
            </div>
          </>
        )}
      </div>

      {can("admin") && (
        <div className="border bg-card p-5 mb-4" data-testid="budgets-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">{t("bg.title")}</div>
          <div className="flex gap-2 items-end mb-5">
            <div className="space-y-1 flex-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("bg.limit")}</Label>
              <Input type="number" value={settings.approval_limit ?? 50000}
                onChange={(e) => setSettings({ ...settings, approval_limit: Number(e.target.value) })}
                className="rounded-none h-8 text-xs" data-testid="settings-approval-limit-input" />
            </div>
            <Button size="sm" onClick={saveCompany} className="rounded-none text-xs font-bold" data-testid="settings-save-limit">{t("common.save")}</Button>
          </div>
          <Label className="text-xs font-bold block mb-2">{t("bg.budgets")}</Label>
          <div className="space-y-2 mb-3">
            {budgets.map((b, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Select value={b.department || "_none"}
                        onValueChange={(v) => { const n = [...budgets]; n[i] = { ...b, department: v === "_none" ? "" : v }; setBudgets(n); }}>
                  <SelectTrigger className="rounded-none h-8 text-xs flex-1" data-testid={`budget-department-${i}`}>
                    <SelectValue placeholder={t("bg.department")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">—</SelectItem>
                    {departments.filter((d) => d.active !== false).map((d) => (
                      <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input type="number" value={b.monthly_budget} placeholder={t("bg.monthly")}
                  onChange={(e) => { const n = [...budgets]; n[i] = { ...b, monthly_budget: e.target.value }; setBudgets(n); }}
                  className="rounded-none h-8 text-xs w-32" data-testid={`budget-amount-${i}`} />
                <span className="text-[10px] text-muted-foreground w-40">
                  {b.spent != null && `${t("bg.spent")}: ${Number(b.spent).toLocaleString()} · ${t("bg.remaining")}: ${Number(b.remaining).toLocaleString()}`}
                </span>
                <button onClick={() => setBudgets(budgets.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-destructive" data-testid={`budget-remove-${i}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="budget-add-button"
              onClick={() => setBudgets([...budgets, { department: "", monthly_budget: 0 }])}>
              <Plus size={12} className="mr-1" /> {t("bg.addRow")}
            </Button>
            <Button size="sm" onClick={saveBudgets} className="rounded-none text-xs font-bold" data-testid="budgets-save-button">{t("common.save")}</Button>
          </div>
        </div>
      )}

      {/* PM Auto-scheduler settings */}
      {(can("admin") || can("maintenance")) && (
        <div className="border bg-card p-5 mb-4" data-testid="pm-auto-section">
          <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Programación automática de PMs</div>
              <div className="text-[11px] text-muted-foreground mt-1">Genera avisos preventivos cuando un equipo se acerca al próximo servicio.</div>
            </div>
            <Button size="sm" variant="outline" onClick={runPmSchedulerNow}
              className="rounded-none text-xs font-bold h-8" data-testid="pm-auto-run-now">
              Ejecutar ahora
            </Button>
          </div>
          <div className="flex items-center gap-4 mb-3">
            <Label className="text-xs font-bold">Activar scheduler</Label>
            <Switch checked={!!pmAuto.enabled} onCheckedChange={(v) => setPmAuto({ ...pmAuto, enabled: v })} data-testid="pm-auto-enabled" />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Anticipación (h)</Label>
              <Input type="number" min={0} value={pmAuto.hours_before}
                onChange={(e) => setPmAuto({ ...pmAuto, hours_before: e.target.value })}
                className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="pm-auto-hours-before" />
              <p className="text-[9px] text-muted-foreground">Crear aviso cuando falten X horas al próximo PM.</p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Intervalo (min)</Label>
              <Input type="number" min={5} value={pmAuto.check_interval_minutes}
                onChange={(e) => setPmAuto({ ...pmAuto, check_interval_minutes: e.target.value })}
                className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="pm-auto-interval" />
              <p className="text-[9px] text-muted-foreground">Frecuencia con la que se revisa la flotilla.</p>
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={savePmAuto} className="rounded-none text-xs font-bold w-full" data-testid="pm-auto-save">
                Guardar
              </Button>
            </div>
          </div>
          <div className="border-t pt-3">
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Endpoint de Telemetría</div>
            <div className="text-[11px] font-mono bg-muted p-2 break-all">
              POST {window.location.origin}/api/mnt/telemetry/reading
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Body JSON: <code className="bg-muted px-1">{`{"equipment_code":"CT-01","reading":1234.5,"api_key":"<TELEMETRY_API_KEY>"}`}</code>
              &nbsp;·&nbsp; también acepta <code className="bg-muted px-1">meter_code</code> en lugar de <code className="bg-muted px-1">equipment_code</code>.
              La clave se configura en <code className="bg-muted px-1">TELEMETRY_API_KEY</code> del backend .env.
            </p>
          </div>
        </div>
      )}

      {/* Stock rotation thresholds */}
      {can("admin") && (
        <div className="border bg-card p-5 mb-4" data-testid="rotation-settings-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">Umbrales de Rotación de Existencias</div>
          <div className="text-[11px] text-muted-foreground mb-3">Días desde la última salida para clasificar artículos.</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Vigente hasta (días)</Label>
              <Input type="number" min={1} value={rotation.vigente_max_days}
                onChange={(e) => setRotation({ ...rotation, vigente_max_days: e.target.value })}
                className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="rotation-vigente-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] font-bold uppercase tracking-widest">Lento hasta (días)</Label>
              <Input type="number" min={2} value={rotation.lento_max_days}
                onChange={(e) => setRotation({ ...rotation, lento_max_days: e.target.value })}
                className="rounded-none h-8 text-xs tabular-nums font-black" data-testid="rotation-lento-input" />
            </div>
            <div className="flex items-end">
              <Button size="sm" onClick={saveRotation} className="rounded-none text-xs font-bold w-full" data-testid="rotation-save-button">
                Guardar
              </Button>
            </div>
          </div>
          <div className="mt-3 space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-widest">Artículos estratégicos (item_number, separados por coma)</Label>
            <Input value={Array.isArray(rotation.strategic_items) ? rotation.strategic_items.join(", ") : (rotation.strategic_items || "")}
              onChange={(e) => setRotation({ ...rotation, strategic_items: e.target.value })}
              placeholder="ITM-100, ITM-200, ..."
              className="rounded-none h-8 text-xs" data-testid="rotation-strategic-input" />
            <p className="text-[9px] text-muted-foreground">
              Vigente (≤ {rotation.vigente_max_days} d) · Lento ({Number(rotation.vigente_max_days) + 1}–{rotation.lento_max_days} d) · Rezagado (&gt; {rotation.lento_max_days} d). Los estratégicos ignoran los umbrales.
            </p>
          </div>
        </div>
      )}

      {/* Reservation cleanup */}
      {can("admin", "warehouse") && (
        <div className="border bg-card p-5 mb-4" data-testid="res-cleanup-section">
          <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Aseo de reservas huérfanas</div>
              <div className="text-[11px] text-muted-foreground mt-1">
                Reconcilia <code className="bg-muted px-1">item_balances.qty_reserved</code> contra avisos preventivos abiertos y OTs vivas. Corre automáticamente cada 24 h.
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={loadResPreview}
                className="rounded-none text-xs font-bold h-8" data-testid="res-preview-refresh">
                Refrescar preview
              </Button>
              <Button size="sm" onClick={runResCleanup}
                className="rounded-none text-xs font-bold h-8" data-testid="res-cleanup-run">
                Ejecutar aseo ahora
              </Button>
            </div>
          </div>

          {resPreview && (
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div className="border p-3 bg-amber-50">
                <div className="text-[9px] uppercase tracking-widest font-bold text-amber-800">A liberar</div>
                <div className="text-2xl font-black tabular-nums text-amber-900" data-testid="res-preview-released">
                  {(resPreview.total_released ?? 0).toFixed(2)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {resPreview.released_count || 0} balance(s) con reserva huérfana
                </div>
              </div>
              <div className="border p-3 bg-blue-50">
                <div className="text-[9px] uppercase tracking-widest font-bold text-blue-800">A crear (falta reserva)</div>
                <div className="text-2xl font-black tabular-nums text-blue-900">
                  {(resPreview.total_missing ?? 0).toFixed(2)}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {resPreview.missing_count || 0} balance(s) sin reserva esperada
                </div>
              </div>
            </div>
          )}

          {resPreview?.releases?.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                Reservas a liberar (top {Math.min(resPreview.releases.length, 20)})
              </div>
              <div className="tbl-wrap max-h-64 overflow-y-auto">
                <table className="tbl">
                  <thead><tr>
                    <th>Artículo</th><th>Almacén</th>
                    <th className="num">Reservado</th>
                    <th className="num">Esperado</th>
                    <th className="num">A liberar</th>
                  </tr></thead>
                  <tbody>
                    {resPreview.releases.slice(0, 20).map((r, i) => (
                      <tr key={i}>
                        <td className="font-mono text-[10px]">{r.item_number}</td>
                        <td>{r.warehouse}</td>
                        <td className="num tabular-nums">{r.actual.toFixed(2)}</td>
                        <td className="num tabular-nums text-muted-foreground">{r.expected.toFixed(2)}</td>
                        <td className="num tabular-nums font-bold text-amber-800">{r.released.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {resHistory.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
                Últimas ejecuciones ({resHistory.length})
              </div>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {resHistory.map((r) => (
                  <div key={r.id} className="text-[11px] flex justify-between border-b py-1">
                    <span>{new Date(r.run_at).toLocaleString()}</span>
                    <span className="tabular-nums">
                      liberó <b>{(r.total_released ?? 0).toFixed(2)}</b> · faltantes <b>{(r.total_missing ?? 0).toFixed(2)}</b>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {can("admin") && (
        <div className="border bg-card p-5 mb-4" data-testid="departments-section">
          <div className="flex justify-between items-start mb-1">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Departamentos</div>
            <Button size="sm" variant="outline" className="rounded-none text-xs font-bold h-7"
                    onClick={() => { setDeptEditId(null); setDeptForm({ code: "", name: "", active: true }); setDeptOpen(true); }}
                    data-testid="dept-inline-new">
              <Plus size={12} className="mr-1" /> Nuevo
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mb-3">Estos departamentos alimentan los desplegables de Vales, Requisiciones, Personal y Presupuestos.</p>
          <div className="border">
            <table className="w-full text-xs">
              <thead className="bg-muted">
                <tr>
                  <th className="p-1 text-left">Código</th>
                  <th className="p-1 text-left">Nombre</th>
                  <th className="p-1 text-left">Estatus</th>
                  <th className="p-1 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {departments.length === 0 && (
                  <tr><td colSpan={4} className="p-3 text-center text-muted-foreground">Sin departamentos</td></tr>
                )}
                {departments.map((d) => (
                  <tr key={d.id} className="border-t" data-testid={`dept-inline-row-${d.id}`}>
                    <td className="p-1 font-bold font-mono">{d.code}</td>
                    <td className="p-1">{d.name}</td>
                    <td className="p-1">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${d.active !== false ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                        {d.active !== false ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="p-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                              onClick={() => { setDeptEditId(d.id); setDeptForm({ code: d.code, name: d.name, active: d.active !== false, default_warehouse: d.default_warehouse || "" }); setDeptOpen(true); }}
                              data-testid={`dept-inline-edit-${d.id}`}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Dialog open={deptOpen} onOpenChange={(o) => { setDeptOpen(o); if (!o) setDeptEditId(null); }}>
            <DialogContent className="rounded-none max-w-md" data-testid="dept-inline-dialog">
              <DialogHeader>
                <DialogTitle>{deptEditId ? "Editar departamento" : "Nuevo departamento"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase">Código</Label>
                  <Input value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value.toUpperCase() })}
                         className="rounded-none h-9 text-xs font-mono" data-testid="dept-inline-code" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase">Nombre</Label>
                  <Input value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                         className="rounded-none h-9 text-xs" data-testid="dept-inline-name" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase">Almacén por defecto (para Vales)</Label>
                  <Input value={deptForm.default_warehouse || ""}
                         onChange={(e) => setDeptForm({ ...deptForm, default_warehouse: e.target.value.toUpperCase() })}
                         placeholder="ALM-01" className="rounded-none h-9 text-xs font-mono"
                         data-testid="dept-inline-default-warehouse" />
                  <div className="text-[9px] text-muted-foreground mt-0.5">
                    Se preselecciona al crear un vale del departamento
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-bold">
                  <input type="checkbox" checked={deptForm.active}
                         onChange={(e) => setDeptForm({ ...deptForm, active: e.target.checked })} />
                  Activo
                </label>
                <div className="flex justify-end">
                  <Button size="sm" className="rounded-none text-xs font-bold"
                          onClick={async () => {
                            try {
                              if (deptEditId) await api.put(`/departments/${deptEditId}`, deptForm);
                              else await api.post("/departments", deptForm);
                              toast.success(t("common.saved"));
                              setDeptOpen(false);
                              api.get("/departments").then((r) => setDepartments(r.data));
                            } catch (e) { toast.error(errMsg(e)); }
                          }}
                          data-testid="dept-inline-save">
                    Guardar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {can("admin") && (
        <div className="border bg-card p-5 mb-4" data-testid="currencies-section">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("cur.title")}</div>
          <p className="text-[10px] text-muted-foreground mb-4">{t("cur.hint")}</p>
          <div className="space-y-2 mb-3">
            {currencies.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={c.code} placeholder={t("cur.code")} maxLength={3} disabled={c.code === "MXN"}
                  onChange={(e) => { const n = [...currencies]; n[i] = { ...c, code: e.target.value.toUpperCase() }; setCurrencies(n); }}
                  className="rounded-none h-8 text-xs w-24 uppercase" data-testid={`currency-code-${i}`} />
                <Input type="number" step="0.0001" value={c.rate_to_base} placeholder={t("cur.rate")} disabled={c.code === "MXN"}
                  onChange={(e) => { const n = [...currencies]; n[i] = { ...c, rate_to_base: e.target.value }; setCurrencies(n); }}
                  className="rounded-none h-8 text-xs w-32" data-testid={`currency-rate-${i}`} />
                <span className="text-[10px] text-muted-foreground">{c.code !== "MXN" && c.rate_to_base ? `1 ${c.code} = ${c.rate_to_base} MXN` : c.code === "MXN" ? "Base" : ""}</span>
                {c.code !== "MXN" && (
                  <button onClick={() => setCurrencies(currencies.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-destructive" data-testid={`currency-remove-${i}`}>
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-none text-xs" data-testid="currency-add-button"
              onClick={() => setCurrencies([...currencies, { code: "", rate_to_base: 1 }])}>
              <Plus size={12} className="mr-1" /> {t("cur.add")}
            </Button>
            <Button size="sm" onClick={saveCurrencies} className="rounded-none text-xs font-bold" data-testid="currencies-save-button">{t("common.save")}</Button>
          </div>
        </div>
      )}

      {can("admin") && <CompanyDocsSection settings={settings} setSettings={setSettings} onSave={saveCompany} />}
      <MySignatureSection users={can("admin") ? users : []} />

      <div className="border bg-card p-5 mb-4" data-testid="password-section">
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">{t("set.changePassword")}</div>
        <div className="flex gap-2 items-end flex-wrap">
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.currentPassword")}</Label>
            <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              className="rounded-none h-8 text-xs w-52" data-testid="current-password-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.newPassword")}</Label>
            <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              className="rounded-none h-8 text-xs w-52" data-testid="new-password-input" />
          </div>
          <Button size="sm" onClick={changePassword} disabled={!pwd.current_password || pwd.new_password.length < 8}
            className="rounded-none text-xs font-bold" data-testid="change-password-button">{t("common.save")}</Button>
        </div>
        <p className="text-[9px] text-muted-foreground mt-2">{t("set.passwordHint")}</p>
      </div>

      {can("admin") && <DigestSettings t={t} />}
      {can("admin") && <ResendSettingsSection t={t} />}
      {can("admin") && <HandoverSection t={t} />}

      {can("admin") && bkSchedule && (
        <div className="border bg-card p-5 mb-4" data-testid="backups-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("bk.title")}</div>
            <div className="flex gap-2">
              <label className="cursor-pointer">
                <input type="file" accept=".gz" className="hidden" onChange={restoreUpload} data-testid="backup-restore-upload-input" />
                <span className="inline-flex items-center h-8 border px-3 text-xs font-medium hover:bg-muted transition-colors">{t("bk.upload")}</span>
              </label>
              <Button size="sm" onClick={runBackup} className="rounded-none text-xs font-bold" data-testid="backup-run-button">{t("bk.now")}</Button>
            </div>
          </div>
          <div className="flex items-center gap-6 flex-wrap mb-4">
            <div className="flex items-center gap-2">
              <Switch checked={bkSchedule.enabled} onCheckedChange={(v) => saveBkSchedule({ enabled: v })} data-testid="backup-schedule-switch" />
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mrp.auto")}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-[10px] font-bold uppercase tracking-widest">{t("mrp.interval")}</Label>
              <Input type="number" min="1" value={bkSchedule.interval_hours}
                onChange={(e) => setBkSchedule({ ...bkSchedule, interval_hours: e.target.value })}
                onBlur={() => saveBkSchedule({})}
                className="rounded-none h-8 text-xs w-20" data-testid="backup-interval-input" />
            </div>
            <span className="text-[10px] text-muted-foreground">{t("mrp.lastRun")}: {bkSchedule.last_run ? new Date(bkSchedule.last_run).toLocaleString("es-MX") : "—"}</span>
          </div>
          {backups.length > 0 && (
            <div className="tbl-wrap" data-testid="backups-table">
              <table className="tbl">
                <thead><tr><th>{t("common.date")}</th><th>{t("bk.file")}</th><th className="num">{t("bk.size")}</th><th></th></tr></thead>
                <tbody>
                  {backups.map((b) => (
                    <tr key={b.name}>
                      <td className="text-muted-foreground">{new Date(b.created_at).toLocaleString("es-MX")}</td>
                      <td className="font-medium">{b.name}</td>
                      <td className="num">{(b.size / 1024).toFixed(1)} KB</td>
                      <td>
                        <div className="flex gap-3">
                          <button onClick={() => downloadReport(`/backups/${b.name}/download`, b.name)}
                            className="text-[10px] font-bold text-primary" data-testid={`backup-download-${b.name}`}>
                            {t("bk.download")}
                          </button>
                          <button onClick={() => restoreBackup(b.name)}
                            className="text-[10px] font-bold text-destructive" data-testid={`backup-restore-${b.name}`}>
                            {t("bk.restore")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {can("admin") && <HierarchyTree />}

      {can("admin") && (
        <div className="border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("set.users")}</div>
            <div className="flex gap-2">
            <BulkUsersDialog t={t} onDone={() => api.get("/users").then((r) => setUsers(r.data))} />
            <Dialog open={uOpen} onOpenChange={setUOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-none text-xs font-bold" data-testid="user-new-button">
                  <Plus size={13} className="mr-1" /> {t("set.newUser")}
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle className="uppercase font-black">{t("set.newUser")}</DialogTitle>
                  <DialogDescription className="text-xs">{t("set.subtitle")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.name")}</Label>
                    <Input value={uForm.name} onChange={(e) => setUForm({ ...uForm, name: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="user-form-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">Email</Label>
                    <Input type="email" value={uForm.email} onChange={(e) => setUForm({ ...uForm, email: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="user-form-email" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.password")}</Label>
                    <Input type="password" value={uForm.password} onChange={(e) => setUForm({ ...uForm, password: e.target.value })}
                      className="rounded-none h-8 text-xs" data-testid="user-form-password" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.role")}</Label>
                    <Select value={uForm.role} onValueChange={(v) => setUForm({ ...uForm, role: v, supplier_id: "" })}>
                      <SelectTrigger className="rounded-none h-8 text-xs" data-testid="user-form-role"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`role.${r}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-bold uppercase tracking-widest">{t("set.position")}</Label>
                    <Input value={uForm.position} onChange={(e) => setUForm({ ...uForm, position: e.target.value })}
                      placeholder="Jefe de Compras" className="rounded-none h-8 text-xs" data-testid="user-form-position" />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer mt-5" data-testid="user-form-welcome">
                    <input type="checkbox" checked={uForm.send_welcome} onChange={(e) => setUForm({ ...uForm, send_welcome: e.target.checked })} />
                    <span className="font-bold">{t("set.bulkWelcome")}</span>
                  </label>
                  {uForm.role === "supplier" && (
                    <div className="space-y-1 col-span-2">
                      <Label className="text-[10px] font-bold uppercase tracking-widest">{t("common.supplier")}</Label>
                      <Select value={uForm.supplier_id} onValueChange={(v) => setUForm({ ...uForm, supplier_id: v })}>
                        <SelectTrigger className="rounded-none h-8 text-xs" data-testid="user-form-supplier"><SelectValue /></SelectTrigger>
                        <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Button onClick={createUser} disabled={!uForm.email || !uForm.password || !uForm.name || (uForm.role === "supplier" && !uForm.supplier_id)}
                  className="rounded-none font-bold" data-testid="user-save-button">{t("common.create")}</Button>
              </DialogContent>
            </Dialog>
            </div>
          </div>
          <div className="tbl-wrap" data-testid="users-table">
            <table className="tbl">
              <thead><tr>
                <th>{t("set.name")}</th>
                <th>Email</th>
                <th>{t("set.position")}</th>
                <th>{t("set.role")}</th>
                <th>Supervisor</th>
                <th className="num">Límite aprobación</th>
                <th>Módulos</th>
                <th>Estatus</th>
                <th></th>
              </tr></thead>
              <tbody>
                {users.map((u) => {
                  const sup = users.find((x) => x.id === u.supervisor_id);
                  const modsCount = (u.visible_modules || []).length;
                  const isActive = u.active !== false;
                  return (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}
                        className={isActive ? "" : "opacity-60"}>
                      <td className="font-medium">{u.name} {u.email === user.email && <span className="text-primary font-bold">·</span>}</td>
                      <td className="text-xs">{u.email}</td>
                      <td className="text-xs text-muted-foreground" data-testid={`user-position-${u.id}`}>{u.position || "—"}</td>
                      <td><span className="uppercase text-[10px] font-bold">{t(`role.${u.role}`)}</span></td>
                      <td className="text-xs text-muted-foreground">{sup ? sup.email : "—"}</td>
                      <td className="num tabular-nums">
                        {u.role === "admin"
                          ? <span className="text-[10px] font-bold text-primary uppercase">Sin tope</span>
                          : (
                            <div className="flex items-center gap-1 justify-end flex-wrap">
                              <span>{Number(u.approval_limit || 0).toLocaleString()} {u.approval_currency || "MXN"}</span>
                              {Number(u.approval_limit || 0) === 0 && (u.role === "approver" || u.role === "buyer" || u.role === "warehouse" || u.role === "maintenance") && (
                                <span title="Sus aprobaciones siempre escalan al supervisor/admin"
                                      className="text-[9px] font-black uppercase px-1 py-0.5 bg-amber-100 text-amber-800 border border-amber-300"
                                      data-testid={`user-escalates-${u.id}`}>
                                  Escala siempre
                                </span>
                              )}
                            </div>
                          )}
                      </td>
                      <td className="text-xs">
                        {u.role === "admin"
                          ? <span className="text-[10px] font-bold text-primary uppercase">Todos</span>
                          : modsCount === 0
                            ? <span className="text-[10px] uppercase text-muted-foreground">Todos (por rol)</span>
                            : <span className="text-[10px] font-bold uppercase">{modsCount} módulos</span>}
                      </td>
                      <td>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${isActive ? "bg-primary/10 text-primary" : "bg-destructive/10 text-destructive"}`}>
                          {isActive ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td>
                        <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                                onClick={() => openPermissions(u)} data-testid={`user-perms-${u.id}`}>
                          <Shield size={12} className="mr-1" /> Editar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* PERMISSIONS DIALOG */}
          <Dialog open={permOpen} onOpenChange={(o) => { setPermOpen(o); if (!o) setPermUser(null); }}>
            <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="user-perms-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield size={16} /> Editar usuario
                </DialogTitle>
                <DialogDescription>
                  {permUser?.email} · <span className="font-bold">{permUser?.role}</span>
                </DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase">Nombre</Label>
                  <Input value={permForm.name} onChange={(e) => setPermForm({ ...permForm, name: e.target.value })}
                         className="rounded-none h-9 text-xs" data-testid="perm-name" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase">{t("set.position")}</Label>
                  <Input value={permForm.position || ""} onChange={(e) => setPermForm({ ...permForm, position: e.target.value })}
                         className="rounded-none h-9 text-xs" data-testid="perm-position" />
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase">Rol</Label>
                  <Select value={permForm.role} onValueChange={(v) => setPermForm({ ...permForm, role: v })}>
                    <SelectTrigger className="rounded-none h-9 text-xs" data-testid="perm-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase">Supervisor (escalamiento)</Label>
                  <Select value={permForm.supervisor_id || "_none"}
                          onValueChange={(v) => setPermForm({ ...permForm, supervisor_id: v === "_none" ? "" : v })}>
                    <SelectTrigger className="rounded-none h-9 text-xs" data-testid="perm-supervisor">
                      <SelectValue placeholder="Sin supervisor (escala directo a admin)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— Sin supervisor —</SelectItem>
                      {users.filter((x) => x.id !== permUser?.id && x.role !== "supplier").map((x) => (
                        <SelectItem key={x.id} value={x.id}>{x.email} · {x.role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[10px] font-bold uppercase">Monto máx. aprobación</Label>
                    <Input type="number" min={0} step="any" value={permForm.approval_limit}
                           onChange={(e) => setPermForm({ ...permForm, approval_limit: Number(e.target.value) })}
                           className="rounded-none h-9 text-xs tabular-nums" data-testid="perm-limit" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase">Moneda</Label>
                    <Select value={permForm.approval_currency}
                            onValueChange={(v) => setPermForm({ ...permForm, approval_currency: v })}>
                      <SelectTrigger className="rounded-none h-9 text-xs" data-testid="perm-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MXN">MXN</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* SECURITY & STATUS */}
              <div className="mt-3 border-t pt-3">
                <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Seguridad y estado</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-bold uppercase">Nueva contraseña (opcional)</Label>
                    <Input type="password" placeholder="Dejar vacío = sin cambio · mín. 8 chars"
                           value={permForm.new_password}
                           onChange={(e) => setPermForm({ ...permForm, new_password: e.target.value })}
                           className="rounded-none h-9 text-xs" data-testid="perm-password"
                           autoComplete="new-password" />
                    <div className="text-[9px] text-muted-foreground mt-1">
                      Al establecer una nueva contraseña, se pedirá al usuario que la cambie en su próximo inicio de sesión.
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase">Estatus de la cuenta</Label>
                    <div className="flex items-center gap-3 h-9">
                      <label className="flex items-center gap-2 text-xs font-bold cursor-pointer">
                        <input type="checkbox" checked={permForm.active}
                               onChange={(e) => setPermForm({ ...permForm, active: e.target.checked })}
                               disabled={permUser?.id === user?.id}
                               data-testid="perm-active" />
                        {permForm.active ? "Activo" : "Inactivo"}
                      </label>
                      {permUser?.id === user?.id && (
                        <span className="text-[9px] text-muted-foreground">(no puedes desactivarte a ti mismo)</span>
                      )}
                    </div>
                    <div className="text-[9px] text-muted-foreground mt-1">
                      Desactivar revoca acceso inmediatamente (incluso sesiones activas).
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-3 border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Módulos visibles ({permForm.visible_modules.length === 0 ? "todos por defecto" : `${permForm.visible_modules.length} seleccionados`})
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-none text-[10px] font-bold h-7"
                            onClick={() => setPermForm({ ...permForm, visible_modules: [] })}
                            data-testid="perm-modules-clear">
                      Todos (default)
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none text-[10px] font-bold h-7"
                            onClick={() => setPermForm({ ...permForm, visible_modules: [...ALL_MODULE_IDS] })}
                            data-testid="perm-modules-all">
                      Marcar todos
                    </Button>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  {MODULE_GROUPS.map((g) => {
                    const groupIds = g.modules.map((m) => m.id);
                    const allSelected = groupIds.every((id) => permForm.visible_modules.includes(id));
                    const someSelected = groupIds.some((id) => permForm.visible_modules.includes(id));
                    return (
                      <div key={g.label} className="border p-2">
                        <button type="button"
                                onClick={() => togglePermGroup(g, allSelected)}
                                className="w-full text-left flex items-center gap-2 mb-1 group"
                                data-testid={`perm-group-${g.label}`}>
                          <input type="checkbox" checked={allSelected}
                                 ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                 readOnly />
                          <span className="text-[10px] font-bold uppercase tracking-widest">{g.label}</span>
                        </button>
                        <div className="grid grid-cols-1 gap-1 pl-4">
                          {g.modules.map((m) => (
                            <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted px-1"
                                   data-testid={`perm-module-${m.id}`}>
                              <input type="checkbox"
                                     checked={permForm.visible_modules.includes(m.id)}
                                     onChange={() => togglePermModule(m.id)} />
                              {m.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-muted-foreground mt-2">
                  Si dejas la lista vacía, el usuario ve todos los módulos permitidos por su rol.
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4 border-t pt-3">
                <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                        onClick={() => setPermOpen(false)} data-testid="perm-cancel">
                  {t("common.cancel")}
                </Button>
                <Button size="sm" className="rounded-none text-xs font-bold"
                        onClick={savePermissions} data-testid="perm-save">
                  {t("common.save")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {user?.role === "admin" && <PoApproversSection />}
      {user?.role === "admin" && <AppLogoSection />}
      {user?.role === "admin" && <PdfLogoSection />}
      {user?.email === "oswaldo.cereceres@oomining.com" && <DangerZoneSection />}
    </div>
  );
}


function AppLogoSection() {
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setVersion(r.data.app_logo_version || "")).catch(() => {}); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Máx 2 MB"); return; }
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post("/settings/app-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVersion(r.data.app_logo_version);
      toast.success("Logo de la plataforma actualizado");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Volver al logo predeterminado (OOMINING)?")) return;
    try {
      await api.delete("/settings/app-logo");
      setVersion("");
      toast.success("Logo restablecido");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-8 border p-4" data-testid="app-logo-section">
      <div className="text-[11px] font-black uppercase tracking-widest mb-2">Logo de la plataforma</div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Se muestra en el menú lateral y en la pantalla de acceso. Recomendado: PNG con fondo transparente, horizontal (≈ 3:1), máx 2 MB.
      </p>
      <div className="flex flex-wrap items-center gap-5">
        <div className="border p-3 bg-white dark:bg-neutral-900 min-w-[200px] flex items-center justify-center">
          <BrandLogo version={version} className="h-16" testid="app-logo-preview" />
        </div>
        <label onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
          className={`flex flex-col items-center justify-center gap-1 border border-dashed px-6 py-4 cursor-pointer text-[11px] ${dragOver ? "border-primary bg-primary/5" : ""}`}
          data-testid="app-logo-dropzone">
          <Upload size={14} />
          <span className="font-bold uppercase tracking-widest">{busy ? "Subiendo…" : "Subir nuevo logo"}</span>
          <span className="text-muted-foreground">PNG · JPG · WebP — clic o arrastra aquí</span>
          <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])} data-testid="app-logo-input" />
        </label>
        {version && (
          <Button size="sm" variant="outline" onClick={remove} className="rounded-none text-xs font-bold" data-testid="app-logo-reset">
            <Trash2 size={12} className="mr-1" /> Restablecer predeterminado
          </Button>
        )}
      </div>
    </div>
  );
}


function PdfLogoSection() {
  const [status, setStatus] = useState({ hasLogo: false, checked: false });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const inputRef = useState(null);

  const checkLogo = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/pdf-logo`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        setPreview(URL.createObjectURL(blob));
        setStatus({ hasLogo: true, checked: true });
      } else {
        setPreview(null);
        setStatus({ hasLogo: false, checked: true });
      }
    } catch { setStatus({ hasLogo: false, checked: true }); }
  };

  useEffect(() => { checkLogo(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Máx 2 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post("/settings/pdf-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo actualizado — aparecerá en todos los PDFs");
      await checkLogo();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar el logo de los PDFs?")) return;
    try {
      await api.delete("/settings/pdf-logo");
      toast.success("Logo eliminado");
      setPreview(null);
      setStatus({ hasLogo: false, checked: true });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-8 border p-4" data-testid="pdf-logo-section">
      <div className="text-[11px] font-black uppercase tracking-widest mb-2">
        Logo corporativo para PDFs
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Se embebe automáticamente en OT, OC, Requisiciones, Vales de Salida y Vales de Transferencia. PNG, JPG o WebP (máx 2 MB).
      </p>
      <div className="flex items-center gap-4">
        {preview ? (
          <img src={preview} alt="Logo PDF" className="max-h-24 max-w-40 border p-2 bg-white" data-testid="pdf-logo-preview" />
        ) : (
          <div className="w-40 h-24 border border-dashed flex items-center justify-center text-[10px] text-muted-foreground">
            Sin logo
          </div>
        )}
        <div className="flex flex-col gap-2">
          <input type="file" accept=".png,.jpg,.jpeg,.webp"
                 onChange={(e) => upload(e.target.files?.[0])}
                 disabled={busy}
                 data-testid="pdf-logo-input"
                 className="text-[11px]" />
          {status.hasLogo && (
            <Button size="sm" variant="destructive" onClick={remove}
                    className="rounded-none text-xs font-bold" data-testid="pdf-logo-delete">
              <Trash2 size={12} className="mr-1" /> Eliminar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


function DangerZoneSection() {
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/system/data-reset");
      setResult(data);
      toast.success(`Reset OK — ${data.deleted_total} registros eliminados`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const downloadBackup = async () => {
    try {
      const { data } = await api.post("/backups/run");
      toast.success(`Backup creado: ${data.name}`);
      // Trigger download
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/backups/${data.name}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` } });
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = data.name;
      a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const importBackup = async (file) => {
    if (!file) return;
    if (!window.confirm("¿Restaurar la BD desde este archivo? Esta acción es irreversible.")) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/backups/restore-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restaurado (${data.restored_collections || 0} colecciones)`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="mt-8 border-2 border-destructive p-4 bg-destructive/5" data-testid="danger-zone">
      <div className="text-[11px] font-black uppercase tracking-widest text-destructive mb-2">
        🚨 Zona peligrosa — Solo usuario maestro
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Acciones destructivas restringidas a <code className="bg-muted px-1">oswaldo.cereceres@oomining.com</code>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border p-3 bg-background">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Exportar BD completa</div>
          <p className="text-[10px] text-muted-foreground mb-2">Genera un backup .json.gz de toda la base y lo descarga.</p>
          <Button size="sm" onClick={downloadBackup} className="rounded-none text-xs font-bold w-full" data-testid="danger-export-btn">
            Exportar y descargar
          </Button>
        </div>

        <div className="border p-3 bg-background">
          <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">Importar BD</div>
          <p className="text-[10px] text-muted-foreground mb-2">Restaura desde un .json.gz previamente exportado.</p>
          <input type="file" accept=".gz,.json"
                 onChange={(e) => importBackup(e.target.files?.[0])}
                 className="text-[10px] w-full" data-testid="danger-import-input" />
        </div>

        <div className="border-2 border-destructive p-3 bg-background">
          <div className="text-[10px] font-bold uppercase tracking-widest text-destructive mb-1">Borrar movimientos</div>
          <p className="text-[10px] text-muted-foreground mb-2">
            Elimina Cardex, OCs, requisiciones, vales, OTs, avisos. Preserva usuarios, artículos, equipos, almacenes y ajustes.
          </p>
          <Button size="sm" variant="destructive" onClick={() => setOpen(true)}
                  className="rounded-none text-xs font-bold w-full" data-testid="danger-reset-btn">
            <Trash2 size={12} className="mr-1" /> Borrar todo (excepto maestros)
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setConfirmText(""); setResult(null); } }}>
        <DialogContent className="rounded-none max-w-lg" data-testid="danger-reset-dialog">
          <DialogHeader>
            <DialogTitle className="uppercase font-black text-destructive">Confirmar borrado</DialogTitle>
          </DialogHeader>
          {!result ? (
            <>
              <p className="text-xs">
                Esta acción borra <b>TODOS los movimientos</b> (Cardex, OCs, requisiciones, vales, OTs, avisos, medidores de lecturas, etc.)
                y no se puede deshacer. Los usuarios y maestros se preservan.
              </p>
              <p className="text-xs font-bold">
                Escribe <code className="bg-muted px-1">BORRAR TODO</code> para confirmar:
              </p>
              <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                     placeholder="BORRAR TODO" className="rounded-none"
                     data-testid="danger-reset-confirm-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="rounded-none text-xs">
                  Cancelar
                </Button>
                <Button size="sm" variant="destructive"
                        disabled={confirmText !== "BORRAR TODO" || busy}
                        onClick={doReset}
                        className="rounded-none text-xs font-bold"
                        data-testid="danger-reset-execute">
                  {busy ? "Borrando…" : "Borrar ahora"}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs font-bold text-emerald-700">✅ Reset completado</p>
              <div className="text-[10px] text-muted-foreground">
                {result.deleted_total} registros eliminados en {Object.keys(result.per_collection || {}).length} colecciones.
              </div>
              <details className="text-[10px]">
                <summary className="cursor-pointer font-bold">Ver detalle</summary>
                <pre className="bg-muted p-2 mt-1 overflow-x-auto">{JSON.stringify(result.per_collection, null, 2)}</pre>
              </details>
              <Button size="sm" onClick={() => { setOpen(false); window.location.reload(); }}
                      className="rounded-none text-xs font-bold" data-testid="danger-reset-done">
                Recargar app
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ResendSettingsSection({ t }) {
  const [dns, setDns] = useState(null);
  const [health, setHealth] = useState(null);
  const [dnsCheck, setDnsCheck] = useState(null);
  const [checkLoading, setCheckLoading] = useState(false);

  useEffect(() => {
    api.get("/resend/dns-records").then((r) => setDns(r.data)).catch(() => setDns(false));
    api.get("/system/health").then((r) => setHealth(r.data)).catch(() => setHealth(null));
  }, []);

  const runDnsCheck = async () => {
    setCheckLoading(true);
    try {
      const r = await api.get("/resend/dns-check");
      setDnsCheck(r.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setCheckLoading(false); }
  };

  if (dns === false) return null;
  if (!dns) return <div className="text-xs text-muted-foreground p-6">Cargando…</div>;

  const isVerified = health && health.resend && health.resend.is_verified_domain;
  const badgeClass = isVerified
    ? "bg-emerald-600 text-white"
    : "bg-amber-500 text-white";
  const badgeText = isVerified ? t("set.verified") : t("set.sandbox");

  return (
    <div className="border bg-card p-5 mb-4" data-testid="resend-settings-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{t("set.resend")}</div>
          <div className="text-[11px] text-muted-foreground">{t("set.resendSubtitle")}</div>
        </div>
        <a href={dns.docs_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-primary flex items-center gap-1 hover:underline" data-testid="resend-docs-link">
          <ExternalLink size={12} /> {t("set.docs")}
        </a>
      </div>

      {/* Current sender + status */}
      <div className="flex items-center gap-3 mb-3 border p-3 bg-muted/30">
        <div className="flex-1">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{t("set.currentSender")}</div>
          <div className="text-sm font-black tabular-nums mt-1" data-testid="resend-current-sender">{dns.current_sender || "—"}</div>
        </div>
        <span className={`px-2 py-1 text-[10px] font-black uppercase tracking-widest ${badgeClass}`} data-testid="resend-status-badge">
          {badgeText}
        </span>
      </div>

      <SenderConfig t={t} currentSender={dns.current_sender} onSaved={() => {
        api.get("/resend/dns-records").then((r) => setDns(r.data));
        api.get("/system/health").then((r) => setHealth(r.data)).catch(() => {});
      }} />

      <ResendDomains t={t} onSenderChanged={() => {
        api.get("/resend/dns-records").then((r) => setDns(r.data));
        api.get("/system/health").then((r) => setHealth(r.data)).catch(() => {});
      }} />

      {/* Steps */}
      <div className="mb-5">
        <div className="text-[10px] font-bold uppercase tracking-widest mb-3">{t("set.steps")}</div>
        <ol className="space-y-2">
          {dns.steps.map((s) => (
            <li key={s.n} className="flex gap-3 text-xs" data-testid={`resend-step-${s.n}`}>
              <span className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground font-black tabular-nums flex items-center justify-center text-[11px]">{s.n}</span>
              <div>
                <div className="font-bold">{s.title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.detail}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Live DNS check */}
      <div className="mt-5 pt-5 border-t">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] font-bold uppercase tracking-widest">{t("set.dnsCheck")}</div>
          <Button size="sm" onClick={runDnsCheck} disabled={checkLoading || (dns && dns.is_sandbox)}
            className="rounded-none text-xs font-bold" data-testid="resend-dns-check-button">
            {checkLoading ? "…" : t("set.dnsCheckNow")}
          </Button>
        </div>
        {dns && dns.is_sandbox && (
          <div className="text-[11px] text-muted-foreground border border-dashed p-3">
            {t("set.dnsSandboxNote")}
          </div>
        )}
        {dnsCheck && (
          <div>
            <div className="flex items-center gap-2 mb-3 text-xs">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t("set.dnsVerdict")}:</span>
              <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${
                dnsCheck.summary?.verdict === "verified" ? "bg-emerald-600 text-white" :
                dnsCheck.summary?.verdict === "partial" ? "bg-amber-500 text-white" :
                "bg-red-600 text-white"}`}>
                {dnsCheck.summary?.verdict || "—"} ({dnsCheck.summary?.ok}/{dnsCheck.summary?.total})
              </span>
              <span className="text-[10px] text-muted-foreground">{dnsCheck.domain}</span>
            </div>
            <div className="space-y-2">
              {(dnsCheck.checks || []).map((c, i) => (
                <div key={i} className="border p-2 text-xs" data-testid={`dns-check-${c.type}-${i}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-block w-2.5 h-2.5 rounded-full ${
                      c.status === "ok" ? "bg-emerald-500" :
                      c.status === "partial" ? "bg-amber-400" :
                      c.status === "error" ? "bg-slate-400" : "bg-red-500"}`}></span>
                    <span className="font-bold">{c.label}</span>
                    <span className="text-[10px] text-muted-foreground font-mono">{c.host}</span>
                    <span className="ml-auto text-[10px] font-black uppercase">{c.status}</span>
                  </div>
                  {c.error ? (
                    <div className="text-[10px] text-red-600 pl-4">{c.error}</div>
                  ) : c.found.length > 0 ? (
                    <div className="text-[10px] font-mono pl-4 break-all text-muted-foreground">
                      {c.found.map((v, j) => <div key={j}>{v}</div>)}
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground pl-4">No se encontraron registros. Esperado: <code className="bg-muted px-1">{c.expected_hint}</code></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
